import React, { useState, useEffect, useMemo } from 'react';
import chemicalService from '../../../services/chemicalService';

const PAYMENT_MODES = ['cash', 'bank_transfer', 'cheque', 'easypaisa', 'jazzcash'];

const PaymentFormModal = ({ defaultType, onClose, onSaved }) => {
  // ─── Type & party master data ──
  const [type, setType] = useState(defaultType || 'receipt'); // 'receipt' | 'payment'
  const [parties, setParties] = useState([]);
  const [loadingParties, setLoadingParties] = useState(false);

  const [invoices, setInvoices] = useState([]); // sales for receipts, purchases for payments
  const [loadingInvoices, setLoadingInvoices] = useState(false);

  const [form, setForm] = useState({
    party_id: '',
    against_id: '',
    date: new Date().toISOString().split('T')[0],
    amount: '',
    mode: 'cash',
    notes: '',
  });

  const [submitting, setSubmitting] = useState(false);

  // ─── Load parties when type changes ──
  useEffect(() => {
    const loadParties = async () => {
      try {
        setLoadingParties(true);
        // Reset downstream state
        setForm((prev) => ({ ...prev, party_id: '', against_id: '', amount: '' }));
        setInvoices([]);

        const fetcher = type === 'receipt'
          ? chemicalService.getCustomers
          : chemicalService.getVendors;
        const res = await fetcher({ page: 1, limit: 1000 });
        // Show only parties with outstanding > 0
        const filtered = (res.data || []).filter(
          (p) => p.is_active && parseFloat(p.outstanding_balance) > 0
        );
        setParties(filtered);
      } catch (err) {
        console.error('Error loading parties:', err);
        alert('❌ Failed to load ' + (type === 'receipt' ? 'customers' : 'vendors'));
      } finally {
        setLoadingParties(false);
      }
    };
    loadParties();
  }, [type]);

  // ─── Load invoices when party changes ──
  useEffect(() => {
    if (!form.party_id) {
      setInvoices([]);
      return;
    }

    const loadInvoices = async () => {
      try {
        setLoadingInvoices(true);
        setForm((prev) => ({ ...prev, against_id: '', amount: '' }));

        const fetcher = type === 'receipt'
          ? chemicalService.getSales
          : chemicalService.getPurchases;

        // Filter by party + non-paid status
        const params = type === 'receipt'
          ? { customer_id: form.party_id, page: 1, limit: 1000 }
          : { vendor_id: form.party_id, page: 1, limit: 1000 };

        const res = await fetcher(params);
        const unsettled = (res.data || []).filter(
          (inv) => inv.payment_status !== 'paid'
        );
        setInvoices(unsettled);
      } catch (err) {
        console.error('Error loading invoices:', err);
        alert('❌ Failed to load invoices/bills');
      } finally {
        setLoadingInvoices(false);
      }
    };
    loadInvoices();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.party_id]);

  // ─── Computed: selected invoice + remaining due ──
  const { selectedInvoice, remainingDue } = useMemo(() => {
    const inv = invoices.find((i) => i.id === parseInt(form.against_id, 10));
    if (!inv) return { selectedInvoice: null, remainingDue: 0 };
    const remaining = parseFloat(inv.grand_total) - parseFloat(inv.amount_paid);
    return { selectedInvoice: inv, remainingDue: remaining };
  }, [invoices, form.against_id]);

  // ─── Auto-fill amount = remaining due when invoice picked ──
  useEffect(() => {
    if (selectedInvoice && !form.amount) {
      setForm((prev) => ({
        ...prev,
        amount: remainingDue.toFixed(2).replace(/\.?0+$/, ''),
      }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedInvoice?.id]);

  // ─── Handlers ──
  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    if (!form.party_id) return alert(`❌ Select a ${type === 'receipt' ? 'customer' : 'vendor'}.`);
    if (!form.against_id) return alert(`❌ Select an ${type === 'receipt' ? 'invoice' : 'bill'}.`);
    if (!form.amount || parseFloat(form.amount) <= 0) return alert('❌ Amount must be > 0.');
    if (parseFloat(form.amount) > remainingDue) {
      return alert(`❌ Amount exceeds remaining due (Rs ${formatNumber(remainingDue)}).`);
    }
    if (!form.date) return alert('❌ Date is required.');

    try {
      setSubmitting(true);
      const payload = {
        type,
        date: form.date,
        party_type: type === 'receipt' ? 'customer' : 'vendor',
        party_id: parseInt(form.party_id, 10),
        against_id: parseInt(form.against_id, 10),
        amount: parseFloat(form.amount),
        mode: form.mode,
        notes: form.notes.trim() || null,
      };

      await chemicalService.createPayment(payload);
      alert(`✅ ${type === 'receipt' ? 'Receipt' : 'Payment'} recorded! Balance updated.`);
      onSaved();
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to save payment';
      alert('❌ ' + msg);
    } finally {
      setSubmitting(false);
    }
  };

  const partyLabel = type === 'receipt' ? 'Customer' : 'Vendor';
  const invoiceLabel = type === 'receipt' ? 'Invoice' : 'Bill';

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>

        <div style={styles.modalHeader}>
          <div>
            <h2 style={styles.modalTitle}>
              {type === 'receipt' ? '↓ Record Receipt' : '↑ Record Payment'}
            </h2>
            <p style={{ color: '#7f8c8d', fontSize: '14px', margin: '4px 0 0 0' }}>
              {type === 'receipt'
                ? 'Record money received from a customer against an invoice'
                : 'Record money paid to a vendor against a bill'}
            </p>
          </div>
          <button onClick={onClose} style={styles.closeButton}>✕</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ padding: '24px 30px' }}>

            {/* ─── Type Selector ────────────── */}
            <div style={styles.typeSwitchWrap}>
              <button
                type="button"
                onClick={() => setType('receipt')}
                style={{
                  ...styles.typeButton,
                  ...(type === 'receipt' ? styles.typeButtonActiveReceipt : {}),
                }}
              >
                ↓ Receipt (from customer)
              </button>
              <button
                type="button"
                onClick={() => setType('payment')}
                style={{
                  ...styles.typeButton,
                  ...(type === 'payment' ? styles.typeButtonActivePayment : {}),
                }}
              >
                ↑ Payment (to vendor)
              </button>
            </div>

            {/* ─── Party Picker ──────────────── */}
            <div style={styles.row}>
              <div style={{ ...styles.formGroup, flex: 2 }}>
                <label style={styles.label}>
                  {partyLabel} <span style={styles.required}>*</span>
                </label>
                <select
                  value={form.party_id}
                  onChange={(e) => handleChange('party_id', e.target.value)}
                  style={styles.input}
                  required
                  disabled={loadingParties}
                >
                  <option value="">
                    {loadingParties
                      ? `Loading ${partyLabel.toLowerCase()}s...`
                      : parties.length === 0
                        ? `-- No ${partyLabel.toLowerCase()}s with outstanding balance --`
                        : `-- Select ${partyLabel.toLowerCase()} --`}
                  </option>
                  {parties.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} — Rs {formatNumber(p.outstanding_balance)} outstanding
                    </option>
                  ))}
                </select>
                {!loadingParties && parties.length === 0 && (
                  <span style={styles.hint}>
                    All {partyLabel.toLowerCase()}s are fully settled.
                  </span>
                )}
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Date <span style={styles.required}>*</span>
                </label>
                <input
                  type="date"
                  value={form.date}
                  onChange={(e) => handleChange('date', e.target.value)}
                  style={styles.input}
                  required
                />
              </div>
            </div>

            {/* ─── Invoice/Bill Picker ──────── */}
            <div style={styles.formGroup}>
              <label style={styles.label}>
                Against {invoiceLabel} <span style={styles.required}>*</span>
              </label>
              {!form.party_id ? (
                <input
                  style={{ ...styles.input, ...styles.inputDisabled }}
                  disabled
                  placeholder={`Select a ${partyLabel.toLowerCase()} first`}
                />
              ) : loadingInvoices ? (
                <input
                  style={{ ...styles.input, ...styles.inputDisabled }}
                  disabled
                  placeholder={`Loading ${invoiceLabel.toLowerCase()}s...`}
                />
              ) : invoices.length === 0 ? (
                <input
                  style={{ ...styles.input, borderColor: '#e74c3c' }}
                  disabled
                  value={`No unsettled ${invoiceLabel.toLowerCase()}s for this ${partyLabel.toLowerCase()}`}
                />
              ) : (
                <select
                  value={form.against_id}
                  onChange={(e) => handleChange('against_id', e.target.value)}
                  style={styles.input}
                  required
                >
                  <option value="">-- Select {invoiceLabel.toLowerCase()} --</option>
                  {invoices.map((inv) => {
                    const refNo = type === 'receipt' ? inv.invoice_no : inv.bill_no;
                    const dateField = type === 'receipt' ? inv.invoice_date : inv.purchase_date;
                    const remaining = parseFloat(inv.grand_total) - parseFloat(inv.amount_paid);
                    return (
                      <option key={inv.id} value={inv.id}>
                        {refNo} ({formatDate(dateField)}) — Rs {formatNumber(remaining)} remaining
                      </option>
                    );
                  })}
                </select>
              )}
            </div>

            {/* ─── Invoice details banner ──── */}
            {selectedInvoice && (
              <div style={styles.invoiceBanner}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '10px' }}>
                  <div>
                    <div style={styles.bannerLabel}>Total</div>
                    <strong>Rs {formatNumber(selectedInvoice.grand_total)}</strong>
                  </div>
                  <div>
                    <div style={styles.bannerLabel}>Already Paid</div>
                    <strong style={{ color: '#27ae60' }}>Rs {formatNumber(selectedInvoice.amount_paid)}</strong>
                  </div>
                  <div>
                    <div style={styles.bannerLabel}>Remaining</div>
                    <strong style={{ color: '#e74c3c' }}>Rs {formatNumber(remainingDue)}</strong>
                  </div>
                  <div>
                    <div style={styles.bannerLabel}>Status</div>
                    <span style={getStatusBadge(selectedInvoice.payment_status)}>
                      {formatStatus(selectedInvoice.payment_status)}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* ─── Amount & Mode ────────────── */}
            <div style={styles.row}>
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Amount (Rs) <span style={styles.required}>*</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  max={remainingDue || undefined}
                  value={form.amount}
                  onChange={(e) => handleChange('amount', e.target.value)}
                  style={{
                    ...styles.input,
                    fontSize: '18px',
                    fontWeight: '700',
                    color: type === 'receipt' ? '#27ae60' : '#e74c3c',
                  }}
                  placeholder="0"
                  required
                  disabled={!selectedInvoice}
                />
                {selectedInvoice && (
                  <span style={styles.hint}>
                    Max Rs {formatNumber(remainingDue)} (remaining due)
                  </span>
                )}
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Payment Mode <span style={styles.required}>*</span></label>
                <select
                  value={form.mode}
                  onChange={(e) => handleChange('mode', e.target.value)}
                  style={styles.input}
                  required
                >
                  {PAYMENT_MODES.map((m) => (
                    <option key={m} value={m}>{formatPaymentMode(m)}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* ─── Settlement preview ──────── */}
            {selectedInvoice && form.amount && parseFloat(form.amount) > 0 && (
              <div style={styles.settlementPreview}>
                After this transaction:&nbsp;
                <strong>
                  Rs {formatNumber(parseFloat(selectedInvoice.amount_paid) + parseFloat(form.amount))}
                </strong> paid of <strong>Rs {formatNumber(selectedInvoice.grand_total)}</strong>
                {parseFloat(form.amount) >= remainingDue ? (
                  <span style={{ color: '#27ae60', fontWeight: '700', marginLeft: '8px' }}>
                    → ✓ Fully Paid
                  </span>
                ) : (
                  <span style={{ color: '#856404', fontWeight: '700', marginLeft: '8px' }}>
                    → Partial (Rs {formatNumber(remainingDue - parseFloat(form.amount))} still remaining)
                  </span>
                )}
              </div>
            )}

            {/* ─── Notes ───────────────────── */}
            <div style={styles.formGroup}>
              <label style={styles.label}>Notes</label>
              <textarea
                value={form.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                style={{ ...styles.input, minHeight: '50px', resize: 'vertical', fontFamily: 'inherit' }}
                placeholder="e.g. Cheque #12345, deposited at HBL"
                rows="2"
              />
            </div>

          </div>

          <div style={styles.modalFooter}>
            <button type="button" onClick={onClose} style={styles.cancelButton} disabled={submitting}>
              Cancel
            </button>
            <button
              type="submit"
              style={{
                ...styles.submitButton,
                background: type === 'receipt' ? '#27ae60' : '#e74c3c',
              }}
              disabled={submitting || !selectedInvoice}
            >
              {submitting
                ? '⏳ Saving...'
                : type === 'receipt' ? '💾 Record Receipt' : '💾 Record Payment'}
            </button>
          </div>
        </form>

      </div>
    </div>
  );
};

// ─── Helpers ──────────────────────────────────────────────

const formatDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatNumber = (n) => {
  const num = parseFloat(n) || 0;
  return num.toLocaleString('en-PK', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
};

const formatPaymentMode = (m) => {
  const map = {
    cash: 'Cash',
    bank_transfer: 'Bank Transfer',
    cheque: 'Cheque',
    easypaisa: 'EasyPaisa',
    jazzcash: 'JazzCash',
  };
  return map[m] || m;
};

const formatStatus = (status) => {
  const map = { paid: '✓ Paid', partial: 'Partial', unpaid: 'Unpaid' };
  return map[status] || status;
};

const getStatusBadge = (status) => {
  const palette = {
    paid:    { bg: '#d4edda', fg: '#155724' },
    partial: { bg: '#fff3cd', fg: '#856404' },
    unpaid:  { bg: '#f8d7da', fg: '#721c24' },
  };
  const c = palette[status] || palette.unpaid;
  return {
    background: c.bg, color: c.fg,
    padding: '4px 10px', borderRadius: '12px',
    fontSize: '11px', fontWeight: '700',
    display: 'inline-block',
  };
};

// ─── Styles ───────────────────────────────────────────────

const styles = {
  modalOverlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, padding: '20px',
  },
  modal: {
    background: 'white', borderRadius: '12px',
    width: '95%', maxWidth: '720px', maxHeight: '92vh',
    overflow: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
  },
  modalHeader: {
    padding: '20px 30px', borderBottom: '2px solid #f0f0f0',
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    position: 'sticky', top: 0, background: 'white', zIndex: 10,
  },
  modalTitle: { fontSize: '22px', fontWeight: '700', color: '#2c3e50', margin: 0 },
  closeButton: {
    padding: '8px 16px', background: '#f0f0f0', border: 'none',
    borderRadius: '8px', cursor: 'pointer',
    fontSize: '20px', fontWeight: '700', color: '#666',
  },

  typeSwitchWrap: {
    display: 'flex',
    gap: '0',
    marginBottom: '20px',
    border: '2px solid #e0e0e0',
    borderRadius: '10px',
    padding: '4px',
    background: '#f8f9fa',
  },
  typeButton: {
    flex: 1,
    padding: '10px 14px',
    background: 'transparent',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '700',
    color: '#7f8c8d',
    transition: 'all 0.2s',
  },
  typeButtonActiveReceipt: {
    background: '#27ae60',
    color: 'white',
  },
  typeButtonActivePayment: {
    background: '#e74c3c',
    color: 'white',
  },

  row: { display: 'flex', gap: '16px', flexWrap: 'wrap' },
  formGroup: {
    flex: 1, minWidth: '180px',
    display: 'flex', flexDirection: 'column',
    gap: '6px', marginBottom: '14px',
  },
  label: { fontSize: '13px', fontWeight: '600', color: '#2c3e50' },
  required: { color: '#e74c3c' },
  input: {
    width: '100%', padding: '10px 12px',
    border: '2px solid #e0e0e0', borderRadius: '8px',
    fontSize: '14px', boxSizing: 'border-box',
    fontFamily: 'inherit',
  },
  inputDisabled: {
    background: '#f5f7fa', color: '#7f8c8d', cursor: 'not-allowed',
  },
  hint: {
    fontSize: '11px', color: '#95a5a6', fontStyle: 'italic',
  },

  invoiceBanner: {
    background: '#f8f9fa',
    border: '1px solid #e0e0e0',
    borderRadius: '10px',
    padding: '14px 16px',
    marginBottom: '14px',
    fontSize: '13px',
  },
  bannerLabel: {
    fontSize: '11px', color: '#7f8c8d', fontWeight: '600',
    textTransform: 'uppercase', letterSpacing: '0.3px',
    marginBottom: '4px',
  },

  settlementPreview: {
    background: '#fffbea',
    color: '#856404',
    padding: '10px 14px',
    borderRadius: '8px',
    fontSize: '13px',
    marginBottom: '14px',
    borderLeft: '4px solid #f39c12',
  },

  modalFooter: {
    padding: '16px 30px',
    borderTop: '2px solid #f0f0f0',
    display: 'flex', justifyContent: 'flex-end', gap: '12px',
    position: 'sticky', bottom: 0, background: 'white',
  },
  cancelButton: {
    padding: '12px 24px',
    background: '#95a5a6', color: 'white',
    border: 'none', borderRadius: '8px',
    cursor: 'pointer', fontSize: '14px', fontWeight: '600',
  },
  submitButton: {
    padding: '12px 28px',
    color: 'white',
    border: 'none', borderRadius: '8px',
    cursor: 'pointer', fontSize: '14px', fontWeight: '600',
  },
};

export default PaymentFormModal;